import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { initPixel } from './lib/fbpixel';
import { initOpenAIAds } from './lib/openaiAds';
import { initRedditPixel } from './lib/redditPixel';
import { initTradedoubler } from './lib/tradedoubler';
import { initPostHog, setupErrorTracking, aliasIdentity } from './lib/posthog';
import { initSentry } from './lib/sentry';
import { captureAttributionFromUrl } from './lib/attribution';
import { rememberReferralFromUrl } from './lib/referralShare';
import '@styles/index.css';

// Initialize Sentry first so any subsequent init failures get captured.
// No-op if VITE_SENTRY_DSN is not set.
initSentry();

// Initialize Meta Pixel (no-op if VITE_FB_PIXEL_ID is not set)
initPixel();

// Initialize OpenAI Ads measurement after Meta so the bridge can mirror the
// existing, centrally managed conversion calls with the same event IDs.
initOpenAIAds();

// Initialize Reddit Pixel — same lifecycle as Meta. Hardcoded default
// pixel ID baked into redditPixel.ts; set VITE_REDDIT_PIXEL_ID="" to
// disable in dev/staging.
initRedditPixel();

// TradeDoubler affiliate SDK. No-op unless the VITE_TRADEDOUBLER_* ids are
// set, and nothing fires until Signup reports the verified sale.
initTradedoubler();

// Initialize PostHog (no-op if VITE_POSTHOG_KEY is not set).
// When the visitor arrives from the marketing-site checkout, the Stripe
// success URL carries ph_distinct_id (the marketing site's PostHog id). Boot
// PostHog as that same person so the post-checkout `purchase` event stitches
// to the pre-checkout funnel across the mediloop.ai → my.mediloop.ai hop.
const phBootstrapDistinctId =
  typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('ph_distinct_id')
    : null;
initPostHog(phBootstrapDistinctId || undefined);
setupErrorTracking();

// ⚠️ 2026-08-08: bootstrap alone is not enough. posthog-js only honours
// `bootstrap.distinctID` when it has NO persisted id yet, so for anyone who
// had ever opened the app before, the incoming marketing distinct_id was
// silently discarded and their marketing session stayed a separate person.
// Aliasing covers that case: it works whether or not an id is already
// persisted, and aliasIdentity() no-ops when the ids already match or when
// this exact alias was sent before.
if (phBootstrapDistinctId) {
  aliasIdentity(phBootstrapDistinctId);
}

// First-touch attribution capture. Runs once at app boot so any landing
// path (Signup, post-OAuth redirect to /onboarding, deep links from email)
// can pick up utm_* and ml_vid from the URL. Idempotent, first-touch is
// sticky in localStorage and never gets overwritten.
captureAttributionFromUrl();
// A friend's referral code on the way in (/start?rc=...), see lib/referralShare.ts.
rememberReferralFromUrl();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </React.StrictMode>,
);
